import { FC, KeyboardEventHandler, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form-latest';
import { ApiService } from '@/support/ApiService.ts';
import { CageApiId } from '@/model/Cage.model.ts';
import { StudyApiId } from '@/model/Study.model.ts';
import { notAborted } from '@/support/Hooks/fetch/useAbortController.ts';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import Button from '@/components/UI/Button';
import { IntakeInputsForm } from '@/components/Workflow/Show/Views/Consumption/IntakeInputsForm.tsx';
import { parseFormFields } from '@/components/Workflow/Show/Views/Consumption/Consumption.utils.ts';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash.ts';
import InVivoError from '@/model/InVivoError.ts';
import {
  ConsumptionInputField,
  ConsumptionMetric,
  ConsumptionMetricCreate,
  ConsumptionMetricStatus,
  ConsumptionType,
} from '@/model/Consumption.model.ts';
import { DateUtils } from '@/utils/Date.utils.ts';
import { errorToast, getCalculationsAndMeasurements } from '@/helpers.tsx';
import { useAlerts, useSelectedCage, useWorkflowSetup } from '@/utils/workflow/useWorkflow.tsx';
import { ISODateTime } from '@/model/Common.model.ts';
import { AnimalAlertV1 } from '@/model/Alert.model.ts';
import styles from '@/components/Workflow/Show/Views/MeasurmentsView.module.scss';
import { Alert } from '@/utils/alerts/Alert.tsx';
import { PresetCalculation } from '@/model/PresetCalculation.model.ts';
import { cageAlertSettings } from '@/components/Workflow/Show/WorkflowConsumption.tsx';

interface IntakeViewProps {
  studyApiId: StudyApiId;
  studyCalculations: Array<PresetCalculation>;
  selectedCageApiId: CageApiId;
  cageIntakeData: Record<
    ConsumptionType,
    {
      data: Array<ConsumptionMetric> | undefined;
      status: ConsumptionMetricStatus;
      enabled: boolean;
    }
  >;
  handleSave: () => void;
  handleAlerts: (alerts: Array<AnimalAlertV1>) => void;
  selectedTime: string;
}

type CageIntakeDetails = Record<
  ConsumptionType,
  {
    todayRecord?: ConsumptionMetric;
    lastIn: number | null;
    inputInDisabled: boolean;
    inputOutDisabled: boolean;
    hasMeasurementsAfterSelectedDate: boolean;
    submitDisabled: boolean;
    focusFields: Array<ConsumptionInputField>;
  }
>;

interface ConsumptionFormData {
  food: { fields: Record<string, number | string | null> };
  fluid: { fields: Record<string, number | string | null> };
  reason_for_change: string | null;
}

type CreateConsumptionRequest = {
  studyId: StudyApiId;
  cageId: CageApiId;
  variables: { metrics: Array<ConsumptionMetricCreate> };
};

const flattenTodayMetrics = (measurements: Array<ConsumptionMetric>): ConsumptionMetric => {
  const flattenedMeasurement = { ...measurements[0] };
  if (measurements.length > 1) {
    flattenedMeasurement.consumption_in = measurements[1].consumption_in;
  }
  return flattenedMeasurement;
};

const getTodayRecord = (
  data: Array<ConsumptionMetric> | undefined,
  dateTime: ISODateTime
): ConsumptionMetric | undefined => {
  let todayRecordValue = undefined;
  const targetDateStr = DateUtils.formatISODateTimeToDate(dateTime);
  if (_notNil(data)) {
    const todayData = data.filter((record) => {
      const recordDateStr = DateUtils.formatISODateTimeToDate(record.recorded_at);
      return recordDateStr === targetDateStr;
    });
    if (_isNotEmpty(todayData)) {
      todayRecordValue = flattenTodayMetrics(todayData);
    }
  }
  return todayRecordValue;
};

const getInputsDisabled = (
  intakeData: Array<ConsumptionMetric> | undefined,
  status: ConsumptionMetricStatus,
  todayRecord: ConsumptionMetric | undefined
): { inputInDisabled: boolean; inputOutDisabled: boolean } => {
  let inputInDisabled = false;
  let inputOutDisabled = true;

  const dataIsEmpty = _isEmpty(intakeData) || _isNil(intakeData);
  if (!dataIsEmpty) {
    if (_isNotEmpty(todayRecord)) {
      inputInDisabled = _notNil(todayRecord.consumption_in) || _notNil(todayRecord.consumed);
      inputOutDisabled = _notNil(todayRecord.consumption_out);
    } else {
      inputOutDisabled = status === 'end';
    }
  }

  return { inputInDisabled, inputOutDisabled };
};

const checkMeasurementsAfterSelectedDate = (data: Array<ConsumptionMetric>, measuredDateTime: ISODateTime) => {
  if (!data || _isEmpty(data) || !measuredDateTime) {
    return false;
  }
  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a.recorded_at).getTime();
    const dateB = new Date(b.recorded_at).getTime();
    return dateB - dateA;
  });
  const latestActualMetric = sortedData[0];

  if (!latestActualMetric) {
    return false;
  }

  const latestMetricDateTime = new Date(latestActualMetric.recorded_at);
  const selectedMeasuredAtDateTime = new Date(measuredDateTime);

  latestMetricDateTime.setSeconds(0, 0);
  selectedMeasuredAtDateTime.setSeconds(0, 0);

  return latestMetricDateTime.getTime() >= selectedMeasuredAtDateTime.getTime();
};

const getFocusFields = (
  type: ConsumptionType,
  inputInDisabled: boolean,
  inputOutDisabled: boolean
): Array<ConsumptionInputField> => {
  const focusFields: Array<ConsumptionInputField> = [];
  if (!inputOutDisabled) {
    focusFields.push(`${type}.fields.input_out`);
    if (type === 'food') {
      focusFields.push(`${type}.fields.spillage`);
    }
  }
  if (!inputInDisabled) {
    focusFields.push(`${type}.fields.input_in`);
  }
  return focusFields;
};

const IntakeView: FC<IntakeViewProps> = ({
  studyApiId,
  studyCalculations,
  selectedCageApiId,
  cageIntakeData,
  handleSave,
  handleAlerts,
  selectedTime,
}) => {
  const { selectedCage } = useSelectedCage();
  const { workflowSetup } = useWorkflowSetup();
  const measured_at = workflowSetup?.measured_at ?? DateUtils.dateNow();
  const combinedDateAndTimeUtc = DateUtils.createDateTimeInUserTimezone(measured_at, selectedTime);
  const { cageAlerts } = useAlerts();
  const calculationMap = useMemo(() => getCalculationsAndMeasurements(studyCalculations), [studyCalculations]);
  const formRef = useRef<HTMLFormElement | null>(null);
  const hasJustSavedRef = useRef(false);
  const [apiError, setApiError] = useState<unknown | false>(false);
  const [defaultValues, setDefaultValues] = useState<ConsumptionFormData>({
    food: { fields: {} },
    fluid: { fields: {} },
    reason_for_change: null,
  });

  const formMethods = useForm<ConsumptionFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });
  const {
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isSubmitting },
  } = formMethods;

  const intakeDetails: CageIntakeDetails = useMemo(() => {
    return (['food', 'fluid'] as Array<ConsumptionType>).reduce((acc, type) => {
      const todayRecord = getTodayRecord(cageIntakeData[type].data, combinedDateAndTimeUtc);
      const lastIn =
        cageIntakeData[type].status === 'end' ? null : (cageIntakeData[type].data?.[0]?.consumption_in ?? null);
      const { inputInDisabled, inputOutDisabled } = getInputsDisabled(
        cageIntakeData[type].data,
        cageIntakeData[type].status,
        todayRecord
      );
      const hasMeasurementsAfterSelectedDate = checkMeasurementsAfterSelectedDate(
        cageIntakeData[type].data ?? [],
        combinedDateAndTimeUtc
      );
      const submitDisabled =
        (inputInDisabled && inputOutDisabled) || hasMeasurementsAfterSelectedDate || !cageIntakeData[type].enabled;
      const focusFields: Array<string> = cageIntakeData[type].enabled
        ? getFocusFields(type, inputInDisabled, inputOutDisabled)
        : [];
      return {
        ...acc,
        [type]: {
          todayRecord,
          lastIn,
          inputInDisabled,
          inputOutDisabled,
          hasMeasurementsAfterSelectedDate,
          submitDisabled,
          focusFields,
        },
      };
    }, {} as CageIntakeDetails);
  }, [cageIntakeData, combinedDateAndTimeUtc]);

  useEffect(() => {
    const newDefaultValues = {
      food: {
        fields: {
          input_out: intakeDetails.food.todayRecord?.consumption_out ?? null,
          input_in: intakeDetails.food.todayRecord?.consumption_in ?? null,
          spillage: intakeDetails.food.todayRecord?.spillage ?? null,
          comment: intakeDetails.food.todayRecord?.comment ?? null,
        },
      },
      fluid: {
        fields: {
          input_out: intakeDetails.fluid.todayRecord?.consumption_out ?? null,
          input_in: intakeDetails.fluid.todayRecord?.consumption_in ?? null,
          comment: intakeDetails.fluid.todayRecord?.comment ?? null,
        },
      },
      reason_for_change: null,
    };
    setDefaultValues(newDefaultValues);
  }, [intakeDetails]);

  useEffect(() => {
    reset(defaultValues);
    hasJustSavedRef.current = false;
  }, [defaultValues, reset, selectedCage]);

  useEffect(() => {
    if (!isSubmitting && !hasJustSavedRef.current) {
      const firstFocusField = intakeDetails.food.focusFields[0] ?? intakeDetails.fluid.focusFields[0];
      if (_notNil(firstFocusField)) {
        setFocus(firstFocusField);
      }
    }
  }, [intakeDetails.food.focusFields, intakeDetails.fluid.focusFields, isSubmitting]);

  const createConsumption = async (request: CreateConsumptionRequest): Promise<Array<ConsumptionMetric>> => {
    const createResponse = await ApiService.call({
      endpoint: 'POST /api/v1/studies/{studyApiId}/cages/{cageApiId}/consumption/bulk',
      path: { studyApiId: request.studyId, cageApiId: request.cageId },
      body: request.variables,
      options: {
        onError: {
          throw: false,
          capture: true,
          toast: false,
          slug: 'consumption-create-bulk',
        },
      },
    });
    if (createResponse.type === 'success') {
      return createResponse.body as Array<ConsumptionMetric>;
    }
    throw new InVivoError('Could not create consumption metrics', {
      cause: createResponse.error,
      slug: 'consumption-create-bulk',
    });
  };

  const onSubmit = async (data: ConsumptionFormData) => {
    setApiError(false);

    try {
      const metrics: Array<ConsumptionMetricCreate> = [];
      for (const type of ['food', 'fluid'] as Array<ConsumptionType>) {
        if (cageIntakeData[type].enabled && !intakeDetails[type].submitDisabled) {
          const fields = data[type]?.fields || {};
          const parsedVariables = parseFormFields(
            { fields },
            intakeDetails[type].inputInDisabled,
            intakeDetails[type].inputOutDisabled,
            intakeDetails[type].lastIn,
            type,
            combinedDateAndTimeUtc,
            data.reason_for_change || null
          );
          if (parsedVariables.errorMessage) {
            errorToast(parsedVariables.errorMessage);
            return;
          }
          metrics.push(parsedVariables);
        }
      }

      if (metrics.length === 0) {
        return;
      }

      try {
        const response = await createConsumption({
          studyId: studyApiId,
          cageId: selectedCageApiId,
          variables: { metrics },
        });
        const alerts = response.flatMap((metric) => metric.animal_alerts || []);
        if (_isNotEmpty(alerts)) {
          handleAlerts(alerts);
        }
        hasJustSavedRef.current = true;
        handleSave();
      } catch (error) {
        if (notAborted(error)) {
          setApiError(error);
        }
      }
    } catch (error) {
      if (notAborted(error)) {
        setApiError(error);
      }
    }
  };

  const handleKeyPress: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      const currentFieldId = event.currentTarget.getAttribute('id');
      const currentFieldType = currentFieldId?.split('.')[0] as ConsumptionType;
      const focusFields = intakeDetails[currentFieldType]?.focusFields;
      const currentIndex = focusFields.findIndex((field) => field === currentFieldId);
      const nextIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
      let nextField = focusFields[nextIndex];
      if (nextIndex > focusFields.length - 1) {
        if (currentFieldType === 'food' && !intakeDetails.fluid.submitDisabled) {
          nextField = intakeDetails['fluid']?.focusFields?.[0];
        } else {
          if (isSubmitting) {
            return;
          }
          handleSubmit(onSubmit)();
        }
      }
      if (nextIndex < 0 && currentFieldType === 'fluid' && !intakeDetails.food.submitDisabled) {
        nextField = intakeDetails['food']?.focusFields?.at(-1) ?? (currentFieldId as ConsumptionInputField);
      }
      if (nextField) {
        setFocus(nextField);
      }
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      {apiError ? (
        <ApiErrorBanner
          className="mw6 mb4"
          error={apiError}
          title="There was an error with your submission"
          text="Please correct the form errors below and try again. If this error persists try again later or contact support."
        />
      ) : null}
      {_notNil(selectedCage) && _isNotEmpty(cageAlerts?.[selectedCage]) && (
        <>
          {(() => {
            const alerts = Object.values(cageAlerts[selectedCage]);
            return (
              <div className={`flex flex-column g3 ${styles.alertsContainer}`}>
                {(['animal_food_intake', 'animal_fluid_intake'] as Array<string>).map((calculation) => {
                  const alertsOfType = alerts.filter(
                    (alert) => alert.calculation === calculation
                  ) as Array<AnimalAlertV1>;
                  if (_isEmpty(alertsOfType)) {
                    return null;
                  }
                  const firstSubject = alertsOfType[0].subject_id;
                  const alertsToDisplay = alertsOfType
                    .filter((alert) => alert.subject_id === firstSubject)
                    .sort((a, b) => {
                      if (a.notification === 'critical' && b.notification !== 'critical') {
                        return -1;
                      }
                      if (b.notification === 'critical' && a.notification !== 'critical') {
                        return 1;
                      }
                      return 0;
                    });
                  return (
                    <>
                      {alertsToDisplay.map((alert: AnimalAlertV1) => (
                        <Alert
                          key={alert.id}
                          alert={alert}
                          calculation={calculationMap.get(alert.calculation)}
                          settings={cageAlertSettings}
                          data-testid={`alert-${alert.id}`}
                        />
                      ))}
                    </>
                  );
                })}
              </div>
            );
          })()}
        </>
      )}
      <FormProvider {...formMethods}>
        <div className="bb b--moon-gray pv3">
          {(['food', 'fluid'] as Array<ConsumptionType>).map(
            (type) =>
              cageIntakeData[type].enabled && (
                <div key={type} className={type === 'food' && cageIntakeData.fluid.enabled ? 'mb4' : ''}>
                  <IntakeInputsForm
                    cageMetricStatus={cageIntakeData[type].status}
                    inputInDisabled={
                      intakeDetails[type].inputInDisabled || intakeDetails[type].hasMeasurementsAfterSelectedDate
                    }
                    inputOutDisabled={
                      intakeDetails[type].inputOutDisabled || intakeDetails[type].hasMeasurementsAfterSelectedDate
                    }
                    lastIn={intakeDetails[type].lastIn}
                    todayRecord={intakeDetails[type].todayRecord}
                    handleKeyPress={handleKeyPress}
                    type={type}
                  />
                </div>
              )
          )}
        </div>
        <div className="pt3">
          <Button
            submit
            disabled={
              isSubmitting ||
              _isNotEmpty(errors) ||
              (intakeDetails.food.submitDisabled && intakeDetails.fluid.submitDisabled)
            }
            testId="workflow-combined-save"
            tooltip={
              intakeDetails.food.hasMeasurementsAfterSelectedDate ||
              intakeDetails.fluid.hasMeasurementsAfterSelectedDate
                ? 'Newer measurements already exist for this cage'
                : undefined
            }
          >
            {isSubmitting && _isEmpty(errors) ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </FormProvider>
    </form>
  );
};

export default IntakeView;
